import React from 'react';
import {CheckCircle2, X, XCircle} from 'lucide-react';
import {Badge} from '@/components/ui/badge';
import {cn} from '@/lib/utils';
import type {IActiveScenarioDetailParticipant} from '@/types/scenarios';

function startCase(s: string) {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

interface FlagsSidebarProps {
    participant: IActiveScenarioDetailParticipant | null;
    isOpen: boolean;
    onClose: () => void;
}

type SidebarFlagAttempt = {
    submitted_response?: string;
    is_correct?: boolean;
    created_at?: string;
};

type SidebarFlag = {
    flag_id: string;
    question?: string;
    score?: number;
    obtained_score?: number;
  obtained?: { obtained_score?: number };
    is_solved?: boolean;
  isSolved?: boolean;
    /** Phase-grouped active-run payload uses this name instead of ``is_solved``. */
    correct_answered?: boolean;
    submitted_responses?: SidebarFlagAttempt[];
  participant_answers?: Array<{
    obtained_score?: number;
    submitted_response?: string;
    updated_at?: string;
    is_correct?: boolean;
  }>;
};

export function FlagsSidebar({participant, isOpen, onClose}: FlagsSidebarProps) {
    if (!isOpen || !participant) {
        return null;
    }

    const p: any = participant as any;
    const phaseFlags: SidebarFlag[] = Array.isArray(p.exercise_phases)
      ? p.exercise_phases.flatMap((phase: any) =>
          Array.isArray(phase?.flags) ? phase.flags : []
        )
      : [];
    const flags: SidebarFlag[] = (
      Array.isArray(p.flags)
        ? p.flags
        : Array.isArray(p.flag_data)
          ? p.flag_data
          : phaseFlags
    ) as SidebarFlag[];

    return (
        <>
            {/* Overlay */}
            <div
                className="fixed inset-0 bg-black/50 z-40 transition-opacity duration-200"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* Sidebar */}
            <div
                className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-card border-l border-border shadow-lg z-50 flex flex-col overflow-hidden transition-transform duration-200">
                {/* Header */}
                <div className="flex items-center justify-between border-b border-border p-4 flex-shrink-0">
                    <div className="flex items-center space-x-3">
                        {participant.avatar ? (
                            <img
                                src={participant.avatar}
                                alt={participant.full_name}
                                className="h-10 w-10 rounded-full object-cover bg-primary/20"
                            />
                        ) : (
                            <div className="h-10 w-10 rounded-full bg-primary/20 text-primary flex items-center justify-center text-sm font-medium">
                                {(participant.full_name || '?').slice(0, 2).toUpperCase()}
                            </div>
                        )}
                        <div className="min-w-0">
                            <h2 className="font-semibold text-card-foreground truncate">
                                {participant.full_name}
                            </h2>
                            <p className="text-xs text-muted-foreground">{participant.team || 'N/A'}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        aria-label="Close sidebar"
                    >
                        <X className="h-5 w-5"/>
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-4">
                    {flags.length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">No flags attempted yet</p>
                    ) : (
                        <div className="space-y-3">
                            {flags.map((flag: SidebarFlag) => {
                                const participantAnswers = flag.participant_answers ?? [];
                                const attempts: SidebarFlagAttempt[] =
                                  flag.submitted_responses ??
                                  participantAnswers.map((a) => ({
                                    submitted_response: a.submitted_response,
                                    is_correct: a.is_correct ?? Number(a.obtained_score ?? 0) > 0,
                                    created_at: a.updated_at,
                                  }));
                                const anyCorrect = attempts.some((a) => Boolean(a.is_correct));
                                const obtainedFromAnswers = participantAnswers.reduce(
                                  (sum, a) => sum + Number(a.obtained_score ?? 0),
                                  0
                                );
                                const obtainedFromNested = Number(flag.obtained?.obtained_score ?? 0);
                                const maxScore = Number(flag.score ?? 0);
                                const obtained = Number(
                                  flag.obtained_score ??
                                  (obtainedFromAnswers > 0 ? obtainedFromAnswers : obtainedFromNested > 0 ? obtainedFromNested : anyCorrect ? maxScore : 0)
                                );
                                // Do not treat ``obtained >= maxScore`` when maxScore is 0 — that is always true and
                                // wrongly marks unscored / manual (0 pt) flags as "Completed" for white-team review.
                                const solved = Boolean(
                                  flag.is_solved ?? flag.isSolved ?? flag.correct_answered,
                                );
                                const isCompleted =
                                  solved || anyCorrect || (maxScore > 0 && obtained >= maxScore);

                                return (
                                    <div key={flag.flag_id} className="border rounded-lg p-4 space-y-2 bg-muted/20">
                                        <div className="flex items-start gap-2 flex-wrap">
                      <span className="font-medium text-sm leading-tight flex-1">
                        {startCase(flag.question ?? '')}
                      </span>
                                            <Badge
                                                variant="outline"
                                                className={cn(
                                                    'text-xs flex-shrink-0',
                                                    isCompleted
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200'
                                                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200'
                                                )}
                                            >
                                                {isCompleted ? 'Completed' : 'In Progress'}
                                            </Badge>
                                        </div>

                                        <p className="text-xs text-muted-foreground">
                                            Score: <strong>{maxScore} pts</strong> · Obtained:
                                            <strong className="text-primary"> {obtained} pts</strong>
                                        </p>

                                        {attempts.length > 0 && (
                                            <div className="mt-3 space-y-2">
                                                <h4 className="font-medium text-xs text-muted-foreground">Attempt History</h4>
                                                {attempts.map((ans, idx) => (
                                                    <div key={idx} className="p-2 rounded-lg border bg-muted/30 text-xs space-y-1">
                                                        <div className="flex items-center gap-2">
                                                            {ans.is_correct ? (
                                                                <CheckCircle2 className="h-3 w-3 text-green-500 flex-shrink-0"/>
                                                            ) : (
                                                                <XCircle className="h-3 w-3 text-amber-500 flex-shrink-0"/>
                                                            )}
                                                            <span className="font-medium">
                                Attempt {idx + 1} {ans.is_correct ? '(Correct)' : '(Incorrect)'}
                              </span>
                                                        </div>
                                                        {ans.submitted_response && (
                                                            <div className="text-muted-foreground break-all">
                                                                Response: <code className="bg-muted px-1 rounded text-xs">{ans.submitted_response}</code>
                                                            </div>
                                                        )}
                                                        {ans.created_at && (
                                                            <div className="text-muted-foreground">
                                                                {new Date(ans.created_at).toLocaleString()}
                                                            </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </>
    );
}
